/**
 * What each keyboard shortcut actually does (docs/06-UI-SPEC.md §16).
 *
 * `shortcuts.ts` says which command a key press is; this file carries it out. Kept
 * apart from React so the whole set can be exercised without a browser.
 *
 * Anything that changes the board is refused when the person may not draw (SRS D4).
 * The server refuses it too — this only stops a student's screen from briefly showing
 * work that is about to be thrown away.
 */

import { canDraw, type PageSummary } from '@opal/shared';

import type { BoardCommand } from './shortcuts.js';
import { useBoardStore } from './store.js';

/** What the board screen lends the shortcuts: the things only it can do. */
export interface CommandDeps {
  /** SRS C6 — per-user undo, as the shared board does it. */
  readonly undo: () => void;
  readonly redo: () => void;
  /** SRS C1 — turns to another page. */
  readonly goToPage: (pageId: string) => void;
  /** §16 — opens and closes the shortcut sheet. */
  readonly setHelp: (open: boolean) => void;
  /** Whether the sheet is open, so Esc closes it before it does anything else. */
  readonly helpOpen: () => boolean;
}

/** SRS C1 — the page before or after this one, or null at either end of the strip. */
export function pageBeside(
  pages: readonly PageSummary[],
  activePageId: string | null,
  direction: 'previous' | 'next',
): string | null {
  const at = pages.findIndex((page) => page.id === activePageId);
  if (at < 0) return null;
  const next = direction === 'previous' ? at - 1 : at + 1;
  return pages[next]?.id ?? null;
}

/** SRS D4 — whether this person may change the board at all. */
function mayEdit(): boolean {
  return canDraw(useBoardStore.getState().access);
}

/** §16 — the shortcuts that act on the chosen work. */
function runOnSelection(command: BoardCommand): void {
  const state = useBoardStore.getState();
  const selection = state.selection;
  if (!selection) return;
  if (command.kind === 'copy') {
    selection.copy();
    return;
  }
  if (!mayEdit()) return;
  if (command.kind === 'cut') selection.cut();
  else if (command.kind === 'duplicate') selection.duplicate();
  else if (command.kind === 'delete') selection.remove();
  else if (command.kind === 'selectAll') {
    // Choosing everything is only useful with the tool that can then act on it.
    state.setTool('select');
    selection.selectAll();
  }
}

/**
 * §16 — Ctrl+V.
 *
 * The select tool comes into the hand with it, so the pasted work arrives held and
 * can be dragged straight where the teacher wants it.
 */
function runPaste(): void {
  if (!mayEdit()) return;
  const state = useBoardStore.getState();
  state.setTool('select');
  useBoardStore.getState().selection?.paste();
}

/** §16 — zoom, and turning the pages. */
function runView(command: BoardCommand, deps: CommandDeps): void {
  const state = useBoardStore.getState();
  if (command.kind === 'zoom') {
    if (command.direction === 'in') state.zoomIn();
    else if (command.direction === 'out') state.zoomOut();
    else state.resetZoom();
    return;
  }
  if (command.kind !== 'page') return;
  const pageId = pageBeside(state.pages, state.activePageId, command.direction);
  if (pageId) deps.goToPage(pageId);
}

/**
 * Esc — closes the sheet if it is open, and otherwise lets go of the selection.
 *
 * One thing at a time, the nearest first: a teacher pressing Esc means "not this",
 * and what they can see is what they mean.
 */
function runCancel(deps: CommandDeps): void {
  if (deps.helpOpen()) {
    deps.setHelp(false);
    return;
  }
  useBoardStore.getState().selection?.clear();
}

/** Carries out one shortcut. */
export function runCommand(command: BoardCommand, deps: CommandDeps): void {
  switch (command.kind) {
    case 'tool':
      useBoardStore.getState().setTool(command.tool);
      return;
    case 'undo':
      deps.undo();
      return;
    case 'redo':
      deps.redo();
      return;
    case 'paste':
      runPaste();
      return;
    case 'copy':
    case 'cut':
    case 'duplicate':
    case 'delete':
    case 'selectAll':
      runOnSelection(command);
      return;
    case 'zoom':
    case 'page':
      runView(command, deps);
      return;
    case 'help':
      deps.setHelp(true);
      return;
    case 'cancel':
      runCancel(deps);
      return;
  }
}
